import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { NavLink } from 'react-router-dom'
import useSWRMutation from 'swr/mutation'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { useAuthStore, postFetcher } from '@/hooks'
import { useRoles } from '@/hooks/auth'
import { Role } from '@/constants/role'
import { ROAS_PAYBACK_PATH, isCeoUser } from '@/constants/ceo'
import { PanelLeft, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { TaskStatusIcon } from '@/components/TaskStatusIcon'
import { useState } from 'react'
import {
  setLastClearTime,
  setLastSilentClearTime,
} from '@/lib/cache-utils'

const NavItem = ({ menu }) => {
  return (
    <NavLink
      to={menu.path}
      className={({ isActive }) =>
        cn(`transition-colors`, {
          'text-primary font-bold': isActive,
          'text-foreground/60 hover:text-primary': !isActive,
        })
      }
    >
      {menu.title}
    </NavLink>
  )
}

const menus = [
  { title: 'ROAS', path: ROAS_PAYBACK_PATH },
  { title: 'Roi', path: '/roi' },
  { title: 'Apple', path: '/apple' },
  { title: 'tRoas', path: '/troas' },
  { title: 'BaseLine', path: '/baseline' },
  { title: 'Admob', path: '/admob' },
  { path: 'admin', title: 'Admin' },
]

/**
 * 清理缓存的通用函数
 * @param {Function} trigger - useSWRMutation 的 trigger 函数
 * @param {boolean} silent - 是否静默清理（不显示 toast）
 * @returns {Promise<number>} 返回删除的缓存数量
 */
export const clearCache = async (trigger, silent = false) => {
  try {
    const result = await trigger({})
    
    // 处理不同的响应格式
    const deletedCount =
      typeof result === 'number'
        ? result
        : result?.deletedCount !== undefined
          ? result.deletedCount
          : 0
    
    // 记录清理时间
    if (silent) {
      setLastSilentClearTime()
    } else {
      setLastClearTime()
    }
    
    if (!silent) {
      const message =
        deletedCount > 0
          ? `缓存清除成功，已删除 ${deletedCount} 条缓存`
          : '缓存清除成功'
      toast.success(message)
    }
    
    return deletedCount
  } catch (error) {
    if (!silent) {
      const errorMessage =
        error?.message || error?.toString() || '清除缓存时发生错误'
      toast.error(errorMessage)
    }
    throw error
  }
}

const ClearCacheButton = () => {
  const { trigger } = useSWRMutation('/cache/clear', postFetcher)
  const [isLoading, setIsLoading] = useState(false)

  const handleClearCache = async () => {
    setIsLoading(true)
    try {
      await clearCache(trigger, false)
    } catch (error) {
      console.error('Clear cache error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Button 
      variant="ghost" 
      size="sm" 
      onClick={handleClearCache}
      disabled={isLoading}
    >
      {isLoading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>清除中...</span>
        </>
      ) : (
        'Clear Cache'
      )}
    </Button>
  )
}

const Nav = () => {
  const user = useAuthStore((state) => state.auth?.user)
  const removeToken = useAuthStore((state) => state.removeToken)
  const isCeo = isCeoUser(user?.id)
  const hasBaselineRole = useRoles([
    Role.Admin,
    Role.BuyerLeader,
    Role.RevenuerLeader,
  ])

  if (!user) {
    return null
  }

  // 根据权限过滤菜单项
  const filteredMenus = menus.filter((menu) => {
    if (menu.path === '/baseline') {
      return hasBaselineRole
    }
    if (menu.path === ROAS_PAYBACK_PATH) {
      return isCeo
    }
    return true
  })

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 p-1 gap-2">
      <div className="container mx-auto flex h-14 items-center">
        <div className="mr-4 hidden lg:flex">
          <a className="mr-6 flex items-center space-x-2" href="/">
            <span className="hidden font-bold sm:inline-block text-primary">
              Monday
            </span>
          </a>
          <nav className="flex items-center gap-4 lg:gap-6">
            {filteredMenus.map((menu) => (
              <NavItem key={menu.path} menu={menu} />
            ))}
          </nav>
        </div>
        <ToggleButton />
        <div className="flex flex-1 items-center justify-between space-x-2 md:justify-end">
          <div className="w-full flex-1 md:w-auto md:flex-none"></div>
          <nav className="flex items-center gap-2">
            <TaskStatusIcon />
            <ClearCacheButton />
            <Avatar
              className="w-8 h-8 border-2 border-transparent hover:border-blue-500 transition-all duration-300"
              onClick={removeToken}
            >
              <AvatarImage src={user.avatarThumb} alt="@shadcn" />
              <AvatarFallback>Log Out</AvatarFallback>
            </Avatar>
          </nav>
        </div>
      </div>
    </header>
  )
}

const SheetNav = ({ menu }) => {
  return (
    <NavLink
      to={menu.path}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-4 px-2.5 text-muted-foreground hover:text-foreground',
          isActive && 'text-primary',
        )
      }
    >
      {menu.title}
    </NavLink>
  )
}

const ToggleButton = () => {
  const isCeo = useAuthStore((state) => isCeoUser(state.auth?.user?.id))
  const hasBaselineRole = useRoles([
    Role.Admin,
    Role.BuyerLeader,
    Role.RevenuerLeader,
  ])

  // 根据权限过滤菜单项
  const filteredMenus = menus.filter((menu) => {
    if (menu.path === '/baseline') {
      return hasBaselineRole
    }
    if (menu.path === ROAS_PAYBACK_PATH) {
      return isCeo
    }
    return true
  })

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          className={cn('lg:hidden h-9 py-2 mr-2 px-0 w-auto')}
          variant="ghost"
          size="icon"
        >
          <PanelLeft className="w-5 h-5" />
          <span className="sr-only">Toggle Menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="sm:max-w-xs">
        <nav className="grid gap-4 text-lg font-medium">
          {filteredMenus.map((menu) => (
            <SheetNav key={menu.path} menu={menu} />
          ))}
        </nav>
      </SheetContent>
    </Sheet>
  )
}

export default Nav
